import React, { useState, useEffect } from "react";
import { Box, Button, Grid, Typography, styled } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import dayjs from "dayjs";
import 'dayjs/locale/th';
import ExcelJS from 'exceljs';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AlertResponse from "../../shared/general/AlertResponse";
import DrawerCustom from "../../shared/general/Drawer";
import DatePickerCustom from "../../shared/date/datePicker";
import ButtonBlue from "../../shared/general/ButtonBlue";
import { getCompensateLeaveReport, getLeaveListReport } from "../../../../actions/report";
import { SelectAllCompaniesVendor } from "./SelectCompanyLists";
import { getAllCompaniesByVendor } from "../../../../actions/vendor";
import { CompensateXlsx } from "./xlsx-export/compensate-xlsx";

const StyledRoot = styled("div")({
    maxWidth: 550,
    padding: 24,
    "& .GridTopicInput": {
        display: "flex",
        alignItems: "center",
    },
});

const StyledFooter = styled("div")({
    padding: 16,
    display: "flex",
    justifyContent: "flex-end",
    "& .cancel": {
        marginRight: 8,
    },
});

const CompensatoryReport = (props) => {

    const { open, handleClose } = props;
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { result: userProfile } = useSelector((state) => state.userProfile);

    const [openAlert, setOpenAlert] = useState(false);
    const [alertType, setAlertType] = useState(false);
    const [filter, setFilter] = useState({
        start: new Date(dayjs().set('date', 1)),
        end: new Date(dayjs().set('date', dayjs().daysInMonth())),
    })

    const [compensateData, setCompensateData] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isFetching, setIsFetching] = useState(true);
    const [LeaveList, setLeaveList] = useState(null);
    const [companyLists, setCompanyLists] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);

    const onChangeCompany = (newValue) => {
        setSelectedCompany(newValue);
    };

    const handleSubmit = () => {
        if (compensateData && compensateData.length > 0) {
            CompensateXlsx(t, compensateData);
        } else {
            handleChangeAlertType(t("NoData"));
            handleOpenAlert(true);
        }
    };

    const fetchData = async () => {
        try {
            setIsFetching(true);
            const response = await getCompensateLeaveReport({
                year: dayjs(selectedYear).format("YYYY"),
                month: dayjs(selectedMonth).format("MM"),
                idCompany: selectedCompany && selectedCompany.idCompany,
            });
            if (response && response.data && response.data.length > 0) {
                setIsFetching(false);
                setCompensateData(response.data);
            } else {
                handleChangeAlertType(t("NoData"));
                handleOpenAlert(true);
                setIsFetching(false);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (userProfile &&selectedYear && selectedMonth && selectedCompany && selectedCompany.idCompany) {
            fetchData();
        } else {
            setIsFetching(false);
        }
    }, [userProfile,selectedYear, selectedMonth, selectedCompany]);

    const handleOpenAlert = () => {
        setOpenAlert(true);
    };

    const handleCloseAlert = () => {
        setOpenAlert(false);
    };

    const handleChangeAlertType = (newValue) => {
        setAlertType(newValue);
    };

    const fetchedCompanyByVendor = async () => {
        try {
            const response = await getAllCompaniesByVendor();
            if (response && response.data) {
                setCompanyLists(response.data);
            } else {
                setCompanyLists(null);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchedCompanyByVendor();
    }, []);

    useEffect(() => {
        if (companyLists && companyLists.length > 0) {
            setSelectedCompany(companyLists[0])
        }
    }, [companyLists]);

    return (
        <DrawerCustom
            title={t("SummaryofLeaveList")}
            anchor="right"
            open={open}
            onClose={handleClose}
        >
            <StyledRoot style={{ width: 400 }}>
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            paddingRight: "16px",
                        }}
                    >
                    </Grid>
                    <Grid item xs={12}>
                        <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("Company")}</Typography>
                        <SelectAllCompaniesVendor
                            options={companyLists ? companyLists : null}
                            value={selectedCompany}
                            disabled={isFetching || !companyLists}
                            onChange={(_, value) => {
                                onChangeCompany(value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">
                            {t("SelectYear")}
                        </Typography>
                        <div className="search-date">
                            <DatePickerCustom
                                inputFormat="YYYY"
                                views={['year']}
                                value={selectedYear}
                                name="start"
                                disabled={isFetching}
                                onChange={(newValue) => {
                                    setSelectedYear(newValue);
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">
                            {t("SelectMonth")}
                        </Typography>
                        <div className="search-date">
                            <DatePickerCustom
                                inputFormat="MMMM"
                                views={['month']}
                                value={selectedMonth}
                                name="start"
                                disabled={isFetching}
                                onChange={(newValue) => {
                                    setSelectedMonth(newValue);
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
                <StyledFooter>
                    <ButtonBlue className="cancel" onClick={handleClose}>
                        {t("Cancel")}
                    </ButtonBlue>
                    <ButtonBlue
                        variant="outlined"
                        startIcon={<DownloadRoundedIcon />}
                        onClick={handleSubmit}
                        disabled={!compensateData || isFetching}
                    >
                        {t("Download")}
                    </ButtonBlue>
                </StyledFooter>
            </StyledRoot>
            <AlertResponse
                open={openAlert}
                handleClose={handleCloseAlert}
                alertType={alertType}
            />
        </DrawerCustom>
    )
}

export default CompensatoryReport;